import { numberHelpers } from '@approvalmax/utils';
import { backend, domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { useMemo } from 'react';
import { useCompanyBasedOnUrl } from 'shared/hooks';

import { useGetRecognizedOcrAttachment } from '../../../hooks/useGetRecognizedOcrAttachment';
import { useOcrData } from '../../../hooks/useOcrData';
import { requestSelector } from '../../../selectors/pageSelectors';
import { getQBooksAccountLineItemsSummary, getQBooksLineItemsSummary, mergeSummaries } from '../../../selectors/qbooks';
import { isSalesInvoiceDetails } from '../../../utils/typeGuards';

const useRequest = <
    Request =
        | domain.QBooksPoRequest
        | domain.QBooksBillRequest
        | domain.QBooksExpenseRequest
        | domain.QBooksInvoiceRequest,
>() => {
    return <Request>useSelector(requestSelector);
};

const useOcr = () => {
    const request = useRequest();

    const ocrAttachment = useGetRecognizedOcrAttachment(request);

    const { data: ocrData } = useOcrData(request, ocrAttachment?.id);

    return ocrData;
};

export const useQBooksRequestItemsSummary = () => {
    const request = useRequest();
    const ocrData = useOcr();

    const { details } = request;

    const company = useCompanyBasedOnUrl();
    const isUSCompany = company.integration?.countryCode === backend.OrganisationVersion.QBO_US;
    const isSalesInvoice = request.integrationCode === domain.IntegrationCode.QBooksInvoice;

    const discount = isSalesInvoiceDetails(details) && details.discount ? details.discount : null;
    const discountType = isSalesInvoiceDetails(details) ? details.discountType : null;
    const applyTaxAfterDiscount = isSalesInvoiceDetails(details) && details.applyTaxAfterDiscount;

    const shipping = isSalesInvoiceDetails(details)
        ? details.lineAmountType === domain.LineAmountType.TaxInclusive
            ? details.shipping.priceGross ?? null
            : details.shipping.price ?? null
        : null;

    const summary = useSelector((state) =>
        isSalesInvoice
            ? getQBooksLineItemsSummary(state, request)
            : mergeSummaries(
                  getQBooksLineItemsSummary(state, request),
                  getQBooksAccountLineItemsSummary(state, request)
              )
    );

    const taxComponents = useMemo(
        () =>
            isSalesInvoice || (!isSalesInvoice && details.lineAmountType !== domain.LineAmountType.NoTax)
                ? summary.taxComponents.filter(({ hidden }) => !hidden)
                : [],
        [isSalesInvoice, details.lineAmountType, summary.taxComponents]
    );

    const totalTax = useMemo(
        () =>
            summary.taxComponents.reduce((prev, curr) => {
                return prev + curr.taxAmount;
            }, 0),
        [summary.taxComponents]
    );

    const showSubtotalWarning = Boolean(
        ocrData?.document?.subTotal && summary.subtotalAmount !== ocrData?.document?.subTotal
    );
    const showTotalWarning = Boolean(ocrData?.document?.total && summary.totalAmount !== ocrData?.document?.total);
    const showTaxTotalWarning =
        numberHelpers.isNumber(ocrData?.document?.totalTax) && totalTax !== ocrData?.document?.totalTax;

    return {
        request,
        shipping,
        summary,
        taxComponents,
        ocrData,
        discount,
        discountType,
        totalTax,
        applyTaxAfterDiscount,
        isUSCompany,
        isSalesInvoice,
        showTotalWarning,
        showTaxTotalWarning,
        showSubtotalWarning,
    };
};
